import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    FaHistory, 
    FaSpinner, 
    FaExclamationTriangle, 
    FaSearch,
    FaCalendarAlt,
    FaChevronLeft,
    FaChevronRight,
    FaEye,
    FaTimes
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './Activities.css';

const ITEMS_PER_PAGE = 100;

const Activities = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const baseUrl = "https://api.burrayhr.com";

    useEffect(() => {
        fetchActivities();
    }, []);

    const fetchActivities = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/reports/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const activitiesData = Array.isArray(response.data) ? response.data : 
                                 response.data.activities ? response.data.activities : [];
            console.log(activitiesData);
            setActivities(activitiesData);
            setError(null);
        } catch (error) {
            console.error('Error fetching activities:', error);
            setError('Failed to fetch activities. Please try again later.');
            setActivities([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            }).format(date);
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const filteredActivities = activities.filter(activity => {
        if (!activity) return false;
        return (activity.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredActivities.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentActivities = filteredActivities.slice(startIndex, endIndex);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleViewDetails = (activity) => {
        setSelectedActivity(activity);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedActivity(null);
    };

    const formatAdditionalData = (data) => {
        if (!data) return null;

        // If data is a string, try to parse it as JSON
        let parsedData = data;
        if (typeof data === 'string') {
            try {
                parsedData = JSON.parse(data);
            } catch (e) {
                // If it's not JSON, return it as is
                return { 'Details': data };
            }
        }

        // Handle different types of data
        if (Array.isArray(parsedData)) {
            // If it's an array of objects (like changes), format each item
            if (parsedData.length > 0 && typeof parsedData[0] === 'object') {
                const formattedChanges = parsedData.map((change, index) => {
                    if (change.field && (change.old_value !== undefined || change.new_value !== undefined)) {
                        return {
                            [`Change ${index + 1}`]: {
                                'Field': change.field,
                                'Previous Value': change.old_value === null ? 'None' : change.old_value,
                                'New Value': change.new_value === null ? 'None' : change.new_value
                            }
                        };
                    }
                    return change;
                });
                return { 'Changes': formattedChanges };
            }
            return { 'Items': parsedData.join(', ') };
        }

        // Format common activity data fields
        const formattedData = {};
        
        // Map common field names to more readable labels
        const fieldLabels = {
            'leave_type': 'Leave Type',
            'start_date': 'Start Date',
            'end_date': 'End Date',
            'status': 'Status',
            'reason': 'Reason',
            'expected_delivery_date': 'Expected Delivery Date',
            'employee': 'Employee',
            'department': 'Department',
            'position': 'Position',
            'action': 'Action',
            'changes': 'Changes Made',
            'old_value': 'Previous Value',
            'new_value': 'New Value',
            'user': 'User',
            'timestamp': 'Time',
            'type': 'Type',
            'details': 'Details',
            'amount': 'Amount',
            'currency': 'Currency',
            'date': 'Date',
            'duration': 'Duration',
            'approved_by': 'Approved By',
            'rejected_by': 'Rejected By',
            'rejection_reason': 'Rejection Reason',
            'approved_at': 'Approved At',
            'rejected_at': 'Rejected At',
            'field': 'Field'
        };

        // Format dates and status values
        const formatValue = (key, value) => {
            if (value === null || value === undefined) return 'N/A';
            
            // Handle nested objects
            if (typeof value === 'object' && !Array.isArray(value)) {
                if (value.field && (value.old_value !== undefined || value.new_value !== undefined)) {
                    return `Changed ${value.field} from "${value.old_value === null ? 'None' : value.old_value}" to "${value.new_value === null ? 'None' : value.new_value}"`;
                }
                return JSON.stringify(value);
            }

            // Format dates
            if (key.toLowerCase().includes('date') || key.toLowerCase().includes('at')) {
                try {
                    return formatDate(value);
                } catch (e) {
                    return value;
                }
            }

            // Format status values
            if (key.toLowerCase().includes('status')) {
                return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
            }

            // Format leave types
            if (key === 'leave_type') {
                return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
            }

            // Format boolean values
            if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
            }

            // Format numbers with currency
            if (key === 'amount' && parsedData.currency) {
                return `${parsedData.currency} ${parseFloat(value).toLocaleString()}`;
            }

            // Format duration
            if (key === 'duration') {
                return `${value} day${value !== 1 ? 's' : ''}`;
            }

            return value;
        };

        // Process the data object
        const processObject = (obj, prefix = '') => {
            Object.entries(obj).forEach(([key, value]) => {
                // Skip empty values and certain fields
                if (value === null || value === undefined || value === '' || 
                    ['id', 'created_at', 'updated_at'].includes(key)) {
                    return;
                }

                const label = fieldLabels[key] || key.split('_').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ');

                if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                    // Handle array of changes
                    value.forEach((change, index) => {
                        if (change.field) {
                            formattedData[`${label} ${index + 1}`] = formatValue('changes', change);
                        }
                    });
                } else if (typeof value === 'object' && value !== null) {
                    // Handle nested objects
                    if (value.field && (value.old_value !== undefined || value.new_value !== undefined)) {
                        formattedData[label] = formatValue('changes', value);
                    } else {
                        processObject(value, `${label} - `);
                    }
                } else {
                    formattedData[prefix + label] = formatValue(key, value);
                }
            });
        };

        processObject(parsedData);
        return formattedData;
    };

    const formatEmployeeUpdateChanges = (data) => {
        if (!data) return null;

        // If data is a string, try to parse it as JSON
        let parsedData = data;
        if (typeof data === 'string') {
            try {
                parsedData = JSON.parse(data);
            } catch (e) {
                return null;
            }
        }

        // Handle changes array
        if (Array.isArray(parsedData)) {
            return parsedData
                .map(change => ({
                    field: change.field,
                    oldValue: change.old_value,
                    newValue: change.new_value,
                    action: change.action || 'updated' // Get the action if available
                }))
                .filter(change => 
                    change.field && 
                    (change.oldValue !== undefined || change.newValue !== undefined)
                );
        }

        // Handle single change object
        if (parsedData.field && (parsedData.old_value !== undefined || parsedData.new_value !== undefined)) {
            return [{
                field: parsedData.field,
                oldValue: parsedData.old_value,
                newValue: parsedData.new_value,
                action: parsedData.action || 'updated'
            }];
        }

        return null;
    };

    const getChangeDescription = (change) => {
        const fieldName = formatFieldName(change.field);
        const oldValue = formatFieldValue(change.field, change.oldValue);
        const newValue = formatFieldValue(change.field, change.newValue);

        // Handle different types of changes
        if (change.oldValue === null && change.newValue !== null) {
            return `Added ${fieldName}: ${newValue}`;
        } else if (change.oldValue !== null && change.newValue === null) {
            return `Removed ${fieldName}: ${oldValue}`;
        } else if (change.action === 'deleted') {
            return `Deleted ${fieldName}`;
        } else if (change.action === 'added') {
            return `Added ${fieldName}: ${newValue}`;
        } else {
            return `${fieldName} changed from ${oldValue} to ${newValue}`;
        }
    };

    const formatFieldName = (field) => {
        const fieldMap = {
            'first_name': 'First Name',
            'last_name': 'Last Name',
            'email': 'Email',
            'phone': 'Phone Number',
            'department': 'Department',
            'position': 'Position',
            'salary': 'Salary',
            'employment_status': 'Employment Status',
            'hire_date': 'Hire Date',
            'end_date': 'End Date',
            'status': 'Status',
            'role': 'Role',
            'manager': 'Manager',
            'address': 'Address',
            'emergency_contact': 'Emergency Contact',
            'bank_details': 'Bank Details',
            'id_number': 'ID Number',
            'passport_number': 'Passport Number',
            'tax_number': 'Tax Number',
            'nationality': 'Nationality',
            'date_of_birth': 'Date of Birth',
            'gender': 'Gender',
            'marital_status': 'Marital Status'
        };

        return fieldMap[field] || field.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const formatFieldValue = (field, value) => {
        if (value === null || value === undefined) return 'None';

        // Format dates
        if (field.toLowerCase().includes('date')) {
            try {
                return formatDate(value);
            } catch (e) {
                return value;
            }
        }

        // Format status values
        if (field.toLowerCase().includes('status')) {
            return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        }

        // Format currency values
        if (field === 'salary') {
            return `$${parseFloat(value).toLocaleString()}`;
        }

        // Format boolean values
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        return value;
    };

    const ActivityModal = ({ activity, onClose }) => {
        if (!activity) return null;

        const isEmployeeUpdate = activity.action_type?.toLowerCase().includes('employee update');
        const changes = isEmployeeUpdate ? formatEmployeeUpdateChanges(activity.additional_data) : null;
        const hasChanges = changes && changes.length > 0;

        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Activity Details</h2>
                        <button className="close-button" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="activity-detail-item">
                            <label>Description:</label>
                            <p>{activity.description || 'No description available'}</p>
                        </div>
                        <div className="activity-detail-item">
                            <label>Date:</label>
                            <p>{formatDate(activity.created_at)}</p>
                        </div>
                        
                        {isEmployeeUpdate ? (
                            hasChanges ? (
                                <div className="activity-detail-item changes-section">
                                    <label>Changes Made:</label>
                                    <div className="changes-list">
                                        {changes.map((change, index) => (
                                            <div key={index} className="change-item">
                                                <div className="change-content">
                                                    <div className="change-description">
                                                        {getChangeDescription(change)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="activity-detail-item">
                                    <p className="no-changes">No changes were made</p>
                                </div>
                            )
                        ) : (
                            activity.additional_data && (
                                <div className="activity-detail-item additional-data-section">
                                    <label>Additional Information:</label>
                                    <div className="additional-data-grid">
                                        {Object.entries(formatAdditionalData(activity.additional_data)).map(([label, value]) => (
                                            <div key={label} className="additional-data-item">
                                                <span className="data-label">{label}:</span>
                                                <span className="data-value">{value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return (
            <div className="pagination">
                <button 
                    className="pagination-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <FaChevronLeft />
                </button>

                {startPage > 1 && (
                    <>
                        <button 
                            className="pagination-button"
                            onClick={() => handlePageChange(1)}
                        >
                            1
                        </button>
                        {startPage > 2 && <span className="pagination-ellipsis">...</span>}
                    </>
                )}

                {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(number => (
                    <button
                        key={number}
                        className={`pagination-button ${number === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(number)}
                    >
                        {number}
                    </button>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
                        <button 
                            className="pagination-button"
                            onClick={() => handlePageChange(totalPages)}
                        >
                            {totalPages}
                        </button>
                    </>
                )}

                <button 
                    className="pagination-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    <FaChevronRight />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="loading-container">
                <Topbar />
                <Sidebar />
                <div className="loading-content">
                    <FaSpinner className="spinner" />
                    <p>Loading activities...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <Topbar />
                <Sidebar />
                <div className="error-content">
                    <FaExclamationTriangle className="error-icon" />
                    <p>{error}</p>
                    <button onClick={fetchActivities} className="retry-button">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="activities-container">
                    <div className="activities-header">
                    <div className="activities-header-left">
                        <div className="title-section">
                            <h1>
                                <FaHistory className="title-icon" />
                                System Activities
                            </h1>
                            <p className="subtitle">Track all activities in the system</p>
                        </div>
                    </div>

                    <div className="search-section">
                        <div className="search-box">
                            <FaSearch style={{color: '#1e3a8a', marginLeft: '10px'}} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search activities..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                        </div>
                    </div>
                    </div>

                    <div className="activities-timeline">
                        {currentActivities.length === 0 ? (
                            <div className="no-activities">
                                <FaHistory className="no-activities-icon" />
                                <p>No activities found</p>
                            </div>
                        ) : (
                            <>
                                {currentActivities.map((activity, index) => (
                                    <div key={index} className="activity-card">
                                        <div className="activity-content">
                                            <p className="activity-description">
                                                {activity.description || 'No description available'}
                                            </p>
                                            <div className="activity-footer">
                                                <div className="activity-time">
                                                    <FaCalendarAlt className="calendar-icon" />
                                                    <span>{formatDate(activity.created_at)}</span>
                                                </div>
                                                <button 
                                                    className="view-details-button"
                                                    onClick={() => handleViewDetails(activity)}
                                                    title="View Details"
                                                >
                                                    <FaEye />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            </div>
            {showModal && (
                <ActivityModal 
                    activity={selectedActivity} 
                    onClose={closeModal}
                />
            )}
        </div>
    );
};

export default Activities;
